import {
  APIFieldAttributes,
  APIFieldType,
  FieldGlobalAttributes,
} from '@ardoq/api-types';
import { getAdvancedSearchOperators, getOperators } from './getOperators';
import { QueryBuilderFilterDefinition } from './types';
import { filterTypeByApiFieldType } from './const';
import { uniqBy } from 'lodash';
import { SearchContext } from '../types';

const getSelectOptions = (
  field: FieldGlobalAttributes
): { label: string; value: string }[] | undefined => {
  switch (field.type) {
    case APIFieldType.LIST:
    case APIFieldType.SELECT_MULTIPLE_LIST:
      return ((field.defaultValue as string) || '').split(',').map(val => ({
        value: val.trim(),
        label: val.trim(),
      }));
    default:
      return undefined;
  }
};

export const fieldLabelToFilterLabel = (fieldLabel: string): string =>
  fieldLabel.replace(/\.EndDate$/, ' end').replace(/\.StartDate$/, ' start');

export const composePerspectiveFilterFromField = (
  field: FieldGlobalAttributes,
  isNewConditionalFormatting: boolean
): QueryBuilderFilterDefinition => ({
  id: field.name,
  label: fieldLabelToFilterLabel(field.label),
  filterType: filterTypeByApiFieldType(isNewConditionalFormatting)[field.type],
  selectOptions: getSelectOptions(field),
  operators: getOperators(field.type),
  numberFormatOptions: field.numberFormatOptions,
});

export const composeFilterFromField = (
  field: FieldGlobalAttributes,
  isNewConditionalFormatting: boolean
): QueryBuilderFilterDefinition => ({
  id: field.name,
  label: fieldLabelToFilterLabel(field.label),
  filterType: filterTypeByApiFieldType(isNewConditionalFormatting)[field.type],
  selectOptions: getSelectOptions(field),
  operators: getAdvancedSearchOperators(field.type),
  numberFormatOptions: field.numberFormatOptions,
});

const filterFieldBySearchContext = (
  field: APIFieldAttributes,
  searchContext: SearchContext
) => {
  if (searchContext === SearchContext.COMPONENT) {
    return Boolean(field.global || field.componentType.length);
  }
  if (searchContext === SearchContext.REFERENCE) {
    return Boolean(field.globalref || field.referenceType.length);
  }
  // SearchContext.WORKSPACE doesn't have any field
  return false;
};

/**
 * TYPE GUARD
 */
const isFieldDataWithWorkspaceSpecificProps = (
  field: FieldGlobalAttributes | APIFieldAttributes
): field is APIFieldAttributes => 'global' in field || 'globalref' in field;

export const composePerspectiveFiltersFromFields = (
  fields: (FieldGlobalAttributes | APIFieldAttributes)[],
  searchContext: SearchContext,
  /** true if the caller is for conditional formatting, where multiselect is supported for {@link APIFieldType.SELECT_MULTIPLE_LIST} rules. false if the caller is for filter rules, which only support a single value for {@link APIFieldType.SELECT_MULTIPLE_LIST} rules. */
  isConditionalFormatting: boolean
) => {
  const fieldsValidInCurrentSearchContext = fields.filter(
    field =>
      !isFieldDataWithWorkspaceSpecificProps(field) ||
      filterFieldBySearchContext(field, searchContext)
  );
  const filtersValidInCurrentSearchContext =
    fieldsValidInCurrentSearchContext.map(field =>
      composePerspectiveFilterFromField(field, isConditionalFormatting)
    );
  return uniqBy(filtersValidInCurrentSearchContext, 'id');
};

export const composeFiltersFromFields = (
  fields: (FieldGlobalAttributes | APIFieldAttributes)[],
  searchContext: SearchContext,
  isNewConditionalFormatting: boolean
) => {
  const fieldsValidInCurrentSearchContext = fields.filter(
    field =>
      !isFieldDataWithWorkspaceSpecificProps(field) ||
      filterFieldBySearchContext(field, searchContext)
  );
  const filtersValidInCurrentSearchContext =
    fieldsValidInCurrentSearchContext.map(field =>
      composeFilterFromField(field, isNewConditionalFormatting)
    );
  return uniqBy(filtersValidInCurrentSearchContext, 'id');
};
